import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { BackupCodesDialog } from "@/components/BackupCodesDialog";

// Reusable 2FA enrolment widget. onDone() fires after successful enable.
export default function TwoFactorSetup({ onDone, compact }) {
  const [stage, setStage] = useState("start"); // start | scan | done
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);

  const begin = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setQr(data.qr);
      setSecret(data.secret);
      setStage("scan");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setCodes(data.backup_codes);
      setStage("done");
      toast.success("Two-factor authentication enabled");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (stage === "start") {
    return (
      <div data-testid="twofa-setup-start">
        <p className="text-slate-300 mb-4">
          Protect your account with an authenticator app (Google Authenticator, Microsoft Authenticator, Authy).
        </p>
        {error && <p className="text-red-300 text-sm mb-3">{error}</p>}
        <button onClick={begin} disabled={busy} data-testid="twofa-begin"
          className="bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] font-bold rounded-full px-6 py-3 transition-colors disabled:opacity-60">
          {busy ? "Preparing…" : "Set up two-factor"}
        </button>
      </div>
    );
  }

  if (stage === "scan") {
    return (
      <form onSubmit={enable} data-testid="twofa-scan">
        <p className="text-slate-300 mb-4">Scan this QR code with your authenticator app, then enter the 6-digit code.</p>
        {qr && <img src={qr} alt="2FA QR code" className="w-44 h-44 rounded-xl bg-white p-2 mb-4" />}
        <p className="text-slate-500 text-xs mb-1">Can't scan? Enter this key manually:</p>
        <code className="text-[#E5C158] text-sm break-all block mb-4">{secret}</code>
        <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric"
          placeholder="123456" data-testid="twofa-verify-code"
          className="w-full max-w-xs card-surface rounded-xl px-4 py-3 text-white text-center text-xl tracking-widest outline-none focus:border-[#C9A84A]/60 mb-4" />
        {error && <p className="text-red-300 text-sm mb-3">{error}</p>}
        <div>
          <button disabled={busy} data-testid="twofa-enable"
            className="bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] font-bold rounded-full px-6 py-3 transition-colors disabled:opacity-60">
            {busy ? "Verifying…" : "Verify & enable"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div data-testid="twofa-done">
      <p className="text-emerald-300 mb-4">Two-factor authentication is now active on your account.</p>
      {codes && <BackupCodesDialog codes={codes} open onClose={() => { setCodes(null); onDone?.(); }} />}
      {!codes && (
        <button onClick={() => onDone?.()} className="bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-full px-6 py-3">
          Continue
        </button>
      )}
    </div>
  );
}
